import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { readStoredUser } from "../utils/sessionUser";
import "./ReservationsWaitlist.css";
import {
  PMS_DEFAULT_LANGUAGE_ID,
  translateReservationStatus,
} from "../utils/pmsLanguages";
import {
  FiAlertCircle,
  FiArrowRight,
  FiCheckCircle,
  FiLink2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";

const STATUS_FILTERS = [
  { key: "all", copyKey: "all" },
  { key: "Pending", copyKey: "pending" },
  { key: "Blocked", copyKey: "blocked" },
  { key: "Converted", copyKey: "converted" },
  { key: "Cancelled", copyKey: "cancelled" },
  { key: "Expired", copyKey: "expired" },
];

const WAITLIST_MESSAGES = {
  en: {
    title: "Waitlist",
    description:
      "Manual entries watch category availability. Linked entries only trigger when their exact reservation is really cancelled or deleted.",
    newEntry: "New Waitlist Entry",
    selectBusinessUnit: "Select a business unit to manage waitlist entries.",
    searchPlaceholder: "Search guest, category, reservation or status...",
    loading: "Loading waitlist...",
    empty: "No waitlist entries match the current filters.",
    table: {
      guest: "Guest",
      stay: "Stay",
      checkIn: "Check-in",
      checkOut: "Check-out",
      roomCategory: "Room Category",
      linkedReservation: "Linked Reservation",
      status: "Status",
      type: "Type",
      actions: "Actions",
    },
    entryTypes: {
      manual: "Manual",
      reservation_linked: "Reservation linked",
    },
    statusFilters: {
      all: "All",
      pending: "Waiting",
      blocked: "Availability secured",
      converted: "Reservation created",
      cancelled: "Removed",
      expired: "Expired",
    },
    statuses: {
      Pending: "Waiting",
      Blocked: "Availability secured",
      Converted: "Reservation created",
      Cancelled: "Removed",
      Expired: "Expired",
    },
    actions: {
      createReservation: "Create reservation",
      delete: "Remove from waitlist",
      removeShort: "Remove",
      retry: "Retry",
      converted: "Reservation created",
      close: "Close",
      cancel: "Cancel",
      select: "Select",
      changeReservation: "Change reservation",
    },
    modal: {
      title: "Create Waitlist Entry",
      copy:
        "Manual entries reserve a category when enough space opens. Linked entries wait for one exact reservation to be cancelled or deleted.",
      waitlistType: "Waitlist Type",
      notes: "Notes",
      notesPlaceholder: "Optional internal note...",
      guestName: "Guest Name",
      guestNamePlaceholder: "Guest full name",
      roomCategory: "Room Category",
      selectCategory: "Select a category",
      loadingCategories: "Loading categories...",
      noCategories: "No room categories are available.",
      categoryLoadError: "Room categories could not be loaded.",
      checkIn: "Check-in",
      checkOut: "Check-out",
      email: "Email",
      emailPlaceholder: "Optional email",
      phone: "Phone",
      phonePlaceholder: "Optional phone",
      searchReservation: "Search Reservation",
      searchReservationPlaceholder: "Search by guest name, email or phone...",
      searchingReservations: "Searching reservations...",
      noReservations: "No reservations found for that search.",
      searchReservationsError: "Reservations could not be searched. Please try again.",
      dateOrderError: "Check-out must be after check-in.",
      checkInPastError: "Check-in cannot be before today.",
      invalidEmailError: "Enter a valid email address or leave the field empty.",
      completeRequiredFields: "Complete the required fields before creating the entry.",
      create: "Create Waitlist Entry",
      creating: "Creating...",
      removeTitle: "Remove waitlist entry?",
      removeCopy:
        "The entry and any availability hold linked to it will be removed. This action cannot be undone.",
      removeConfirm: "Remove entry",
      removing: "Removing...",
    },
    selection: {
      statusPrefix: "Status",
      roomPrefix: "Room",
      noCategory: "No category",
      noEmail: "No email",
      noPhone: "No phone",
      splitStay: "Split stay: an entry will be created for each stay segment.",
      disabledReasons: {
        noCategory: "Reservation has no room category",
        incompleteDates: "Reservation has incomplete stay dates",
        cancelled: "Reservation is cancelled",
        checkedIn: "Reservation is already checked in",
        alreadyOnWaitlist: "Already on waitlist",
        beforeToday: "Reservation check-in date is before today",
        inPast: "Reservation is in the past",
        draft: "Reservation is still draft",
      },
    },
    messages: {
      loadError: "Error loading waitlist",
      createSuccess: "Waitlist entry created successfully.",
      createError: "Error creating waitlist entry",
      removeSuccess: "Waitlist entry removed.",
      removeError: "Error removing waitlist entry",
      openDraftError: "Error opening reservation draft",
      businessUnitSyncError:
        "The business unit session could not be updated. Return to your active unit and try again.",
      refreshWarning:
        "The action succeeded, but the list could not refresh. The data shown may be outdated.",
      duplicateEntry: "This reservation already has an active waitlist entry.",
      removeStateConflict:
        "Only entries waiting or with availability secured can be removed.",
      openDraftRemovalConflict:
        "This entry is protecting an open reservation draft. Close or confirm the draft before removing it.",
    },
  },
  es: {
    title: "Lista de espera",
    description:
      "Las entradas manuales vigilan la disponibilidad de la categoría. Las vinculadas solo se activan cuando su reserva exacta se cancela o elimina realmente.",
    newEntry: "Nueva entrada de lista de espera",
    selectBusinessUnit: "Selecciona una unidad de negocio para gestionar la lista de espera.",
    searchPlaceholder: "Buscar huésped, categoría, reserva o estado...",
    loading: "Cargando lista de espera...",
    empty: "No hay entradas de lista de espera que coincidan con los filtros actuales.",
    table: {
      guest: "Huésped",
      stay: "Estancia",
      checkIn: "Check-in",
      checkOut: "Check-out",
      roomCategory: "Categoría de habitación",
      linkedReservation: "Reserva vinculada",
      status: "Estado",
      type: "Tipo",
      actions: "Acciones",
    },
    entryTypes: {
      manual: "Manual",
      reservation_linked: "Vinculada a reserva",
    },
    statusFilters: {
      all: "Todas",
      pending: "En espera",
      blocked: "Disponibilidad asegurada",
      converted: "Reserva creada",
      cancelled: "Retirada",
      expired: "Caducada",
    },
    statuses: {
      Pending: "En espera",
      Blocked: "Disponibilidad asegurada",
      Converted: "Reserva creada",
      Cancelled: "Retirada",
      Expired: "Caducada",
    },
    actions: {
      createReservation: "Crear reserva",
      delete: "Retirar de la lista",
      removeShort: "Retirar",
      retry: "Reintentar",
      converted: "Reserva creada",
      close: "Cerrar",
      cancel: "Cancelar",
      select: "Seleccionar",
      changeReservation: "Cambiar reserva",
    },
    modal: {
      title: "Crear entrada de lista de espera",
      copy:
        "Las entradas manuales reservan una categoría cuando se libera espacio suficiente. Las vinculadas esperan a que una reserva exacta se cancele o elimine.",
      waitlistType: "Tipo de lista de espera",
      notes: "Notas",
      notesPlaceholder: "Nota interna opcional...",
      guestName: "Nombre del huésped",
      guestNamePlaceholder: "Nombre completo del huésped",
      roomCategory: "Categoría de habitación",
      selectCategory: "Selecciona una categoría",
      loadingCategories: "Cargando categorías...",
      noCategories: "No hay categorías de habitación disponibles.",
      categoryLoadError: "No se han podido cargar las categorías de habitación.",
      checkIn: "Check-in",
      checkOut: "Check-out",
      email: "Email",
      emailPlaceholder: "Email opcional",
      phone: "Teléfono",
      phonePlaceholder: "Teléfono opcional",
      searchReservation: "Buscar reserva",
      searchReservationPlaceholder: "Buscar por nombre, email o teléfono...",
      searchingReservations: "Buscando reservas...",
      noReservations: "No se han encontrado reservas para esa búsqueda.",
      searchReservationsError: "No se han podido buscar las reservas. Inténtalo de nuevo.",
      dateOrderError: "El check-out debe ser posterior al check-in.",
      checkInPastError: "El check-in no puede ser anterior a hoy.",
      invalidEmailError: "Introduce un email válido o deja el campo vacío.",
      completeRequiredFields: "Completa los campos obligatorios antes de crear la entrada.",
      create: "Crear entrada de lista de espera",
      creating: "Creando...",
      removeTitle: "¿Retirar la entrada de la lista?",
      removeCopy:
        "La entrada y cualquier bloqueo de disponibilidad vinculado se retirarán. Esta acción no se puede deshacer.",
      removeConfirm: "Retirar entrada",
      removing: "Retirando...",
    },
    selection: {
      statusPrefix: "Estado",
      roomPrefix: "Habitación",
      noCategory: "Sin categoría",
      noEmail: "Sin email",
      noPhone: "Sin teléfono",
      splitStay: "Estancia dividida: se creará una entrada por cada tramo de la estancia.",
      disabledReasons: {
        noCategory: "La reserva no tiene categoría de habitación",
        incompleteDates: "La reserva tiene fechas de estancia incompletas",
        cancelled: "La reserva está cancelada",
        checkedIn: "La reserva ya ha hecho check-in",
        alreadyOnWaitlist: "Ya está en la lista de espera",
        beforeToday: "El check-in de la reserva es anterior a hoy",
        inPast: "La reserva ya está en el pasado",
        draft: "La reserva sigue siendo un borrador",
      },
    },
    messages: {
      loadError: "Error al cargar la lista de espera",
      createSuccess: "Entrada de lista de espera creada correctamente.",
      createError: "Error al crear la entrada de lista de espera",
      removeSuccess: "Entrada de lista de espera eliminada.",
      removeError: "Error al eliminar la entrada de lista de espera",
      openDraftError: "Error al abrir el borrador de reserva",
      businessUnitSyncError:
        "No se ha podido actualizar la sesión de la unidad de negocio. Vuelve a tu unidad activa e inténtalo de nuevo.",
      refreshWarning:
        "La acción se ha completado, pero la lista no se ha podido actualizar. Los datos mostrados pueden estar desactualizados.",
      duplicateEntry: "Esta reserva ya tiene una entrada activa en la lista de espera.",
      removeStateConflict:
        "Solo se pueden retirar entradas en espera o con disponibilidad asegurada.",
      openDraftRemovalConflict:
        "Esta entrada está protegiendo un borrador de reserva abierto. Cierra o confirma el borrador antes de retirarla.",
    },
  },
  ca: {
    title: "Llista d'espera",
    description:
      "Les entrades manuals vigilen la disponibilitat de la categoria. Les vinculades només s'activen quan la seva reserva exacta es cancel·la o s'elimina realment.",
    newEntry: "Nova entrada de llista d'espera",
    selectBusinessUnit: "Selecciona una unitat de negoci per gestionar la llista d'espera.",
    searchPlaceholder: "Cerca hoste, categoria, reserva o estat...",
    loading: "Carregant la llista d'espera...",
    empty: "No hi ha entrades de la llista d'espera que coincideixin amb els filtres actuals.",
    table: {
      guest: "Hoste",
      stay: "Estada",
      checkIn: "Check-in",
      checkOut: "Check-out",
      roomCategory: "Categoria d'habitació",
      linkedReservation: "Reserva vinculada",
      status: "Estat",
      type: "Tipus",
      actions: "Accions",
    },
    entryTypes: {
      manual: "Manual",
      reservation_linked: "Vinculada a reserva",
    },
    statusFilters: {
      all: "Totes",
      pending: "En espera",
      blocked: "Disponibilitat assegurada",
      converted: "Reserva creada",
      cancelled: "Retirada",
      expired: "Caducada",
    },
    statuses: {
      Pending: "En espera",
      Blocked: "Disponibilitat assegurada",
      Converted: "Reserva creada",
      Cancelled: "Retirada",
      Expired: "Caducada",
    },
    actions: {
      createReservation: "Crear reserva",
      delete: "Retirar de la llista",
      removeShort: "Retirar",
      retry: "Tornar-ho a provar",
      converted: "Reserva creada",
      close: "Tancar",
      cancel: "Cancel\u00b7lar",
      select: "Seleccionar",
      changeReservation: "Canviar reserva",
    },
    modal: {
      title: "Crear entrada de llista d'espera",
      copy:
        "Les entrades manuals reserven una categoria quan s'allibera prou espai. Les vinculades esperen que una reserva exacta es cancel·li o s'elimini.",
      waitlistType: "Tipus de llista d'espera",
      notes: "Notes",
      notesPlaceholder: "Nota interna opcional...",
      guestName: "Nom de l'hoste",
      guestNamePlaceholder: "Nom complet de l'hoste",
      roomCategory: "Categoria d'habitació",
      selectCategory: "Selecciona una categoria",
      loadingCategories: "Carregant categories...",
      noCategories: "No hi ha categories d'habitació disponibles.",
      categoryLoadError: "No s'han pogut carregar les categories d'habitació.",
      checkIn: "Check-in",
      checkOut: "Check-out",
      email: "Email",
      emailPlaceholder: "Email opcional",
      phone: "Telèfon",
      phonePlaceholder: "Telèfon opcional",
      searchReservation: "Cerca reserva",
      searchReservationPlaceholder: "Cerca per nom, email o telèfon...",
      searchingReservations: "Cercant reserves...",
      noReservations: "No s'han trobat reserves per a aquesta cerca.",
      searchReservationsError: "No s'han pogut cercar les reserves. Torna-ho a provar.",
      dateOrderError: "El check-out ha de ser posterior al check-in.",
      checkInPastError: "El check-in no pot ser anterior a avui.",
      invalidEmailError: "Introdueix un email vàlid o deixa el camp buit.",
      completeRequiredFields: "Completa els camps obligatoris abans de crear l'entrada.",
      create: "Crear entrada de llista d'espera",
      creating: "Creant...",
      removeTitle: "Vols retirar l'entrada de la llista?",
      removeCopy:
        "L'entrada i qualsevol bloqueig de disponibilitat vinculat es retiraran. Aquesta acció no es pot desfer.",
      removeConfirm: "Retirar entrada",
      removing: "Retirant...",
    },
    selection: {
      statusPrefix: "Estat",
      roomPrefix: "Habitació",
      noCategory: "Sense categoria",
      noEmail: "Sense email",
      noPhone: "Sense telèfon",
      splitStay: "Estada dividida: es crearà una entrada per cada tram de l'estada.",
      disabledReasons: {
        noCategory: "La reserva no té categoria d'habitació",
        incompleteDates: "La reserva té dates d'estada incompletes",
        cancelled: "La reserva està cancel·lada",
        checkedIn: "La reserva ja ha fet el check-in",
        alreadyOnWaitlist: "Ja és a la llista d'espera",
        beforeToday: "El check-in de la reserva és anterior a avui",
        inPast: "La reserva ja és en el passat",
        draft: "La reserva encara és un esborrany",
      },
    },
    messages: {
      loadError: "Error en carregar la llista d'espera",
      createSuccess: "Entrada de llista d'espera creada correctament.",
      createError: "Error en crear l'entrada de llista d'espera",
      removeSuccess: "Entrada de llista d'espera eliminada.",
      removeError: "Error en eliminar l'entrada de llista d'espera",
      openDraftError: "Error en obrir l'esborrany de reserva",
      businessUnitSyncError:
        "No s'ha pogut actualitzar la sessió de la unitat de negoci. Torna a la unitat activa i prova-ho de nou.",
      refreshWarning:
        "L'acció s'ha completat, però la llista no s'ha pogut actualitzar. Les dades mostrades poden estar desactualitzades.",
      duplicateEntry: "Aquesta reserva ja té una entrada activa a la llista d'espera.",
      removeStateConflict:
        "Només es poden retirar entrades en espera o amb disponibilitat assegurada.",
      openDraftRemovalConflict:
        "Aquesta entrada està protegint un esborrany de reserva obert. Tanca o confirma l'esborrany abans de retirar-la.",
    },
  },
};


function formatDisplayDate(value) {
  if (!value) return "-";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
}

function getTodayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysToIsoDate(value, days) {
  if (!value) return "";
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function getWaitlistStatusClass(status) {
  if (status === "Blocked") return "blocked";
  if (status === "Converted") return "converted";
  if (status === "Cancelled") return "cancelled";
  if (status === "Expired") return "expired";
  return "pending";
}

function buildStayLabel(row) {
  return `${formatDisplayDate(row.check_in_date)} - ${formatDisplayDate(row.check_out_date)}`;
}

function getWaitlistStatusLabel(status, waitlistCopy) {
  return waitlistCopy?.statuses?.[status] || String(status || "-");
}

function getEntryTypeLabel(entryType, waitlistCopy) {
  return (
    waitlistCopy?.entryTypes?.[entryType] ||
    (entryType === "reservation_linked" ? "Reservation linked" : "Manual")
  );
}

function getDisabledReasonLabel(reason, waitlistCopy) {
  if (!reason) return "";
  const disabledReasons = waitlistCopy?.selection?.disabledReasons || {};
  const exactReasonKeys = {
    "Reservation has no room category": "noCategory",
    "Reservation has incomplete stay dates": "incompleteDates",
    "Reservation is cancelled": "cancelled",
    "Reservation is already checked in": "checkedIn",
    "Reservation check-in date is before today": "beforeToday",
    "Reservation is in the past": "inPast",
    "Reservation is still draft": "draft",
  };
  const exactKey = exactReasonKeys[reason];
  if (exactKey && disabledReasons[exactKey]) return disabledReasons[exactKey];

  const alreadyOnWaitlistMatch = String(reason).match(/^Already on waitlist \((.+)\)$/);
  if (alreadyOnWaitlistMatch) {
    const status = getWaitlistStatusLabel(alreadyOnWaitlistMatch[1], waitlistCopy);
    return `${disabledReasons.alreadyOnWaitlist || "Already on waitlist"} (${status})`;
  }

  return reason;
}

function getLocalizedRequestError(error, fallback, languageId) {
  const serverMessage = String(error?.response?.data?.error || "").trim();
  if (languageId === PMS_DEFAULT_LANGUAGE_ID && serverMessage) return serverMessage;
  return fallback;
}

function getWaitlistRowToneClass(status) {
  if (status === "Blocked") return "wl-row--blocked";
  if (status === "Converted") return "wl-row--converted";
  if (status === "Cancelled") return "wl-row--cancelled";
  if (status === "Expired") return "wl-row--expired";
  return "wl-row--pending";
}

const defaultManualForm = {
  guest_name: "",
  guest_email: "",
  guest_phone: "",
  room_category_id: "",
  check_in_date: "",
  check_out_date: "",
  notes: "",
};

const ReservationsWaitlist = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "";
  const { themeId, language, languageId } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const waitlistCopy =
    language?.messages?.waitlist ||
    WAITLIST_MESSAGES[languageId] ||
    WAITLIST_MESSAGES[PMS_DEFAULT_LANGUAGE_ID];
  const [currentUser, setCurrentUser] = useState(() => readStoredUser());
  const permissions = currentUser?.permissions || {};
  const isAdminRole = Number(currentUser?.id) === 1;
  const canViewWaitlist = isAdminRole || Boolean(permissions["reservations.view_list"]);
  const canCreateWaitlist = isAdminRole || Boolean(permissions["reservations.create"]);
  const canUpdateWaitlist = isAdminRole || Boolean(permissions["reservations.update"]);
  const sessionBusinessUnitReady = Boolean(
    currentUser?.id &&
      businessUnitId &&
      Number(currentUser?.active_business_unit_id) === Number(businessUnitId)
  );

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(false);
  const [categoryReloadToken, setCategoryReloadToken] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [modalError, setModalError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [entryType, setEntryType] = useState("manual");
  const [manualForm, setManualForm] = useState(defaultManualForm);
  const [linkedSearch, setLinkedSearch] = useState("");
  const [debouncedLinkedSearch, setDebouncedLinkedSearch] = useState("");
  const [reservationResults, setReservationResults] = useState([]);
  const [reservationSearchLoading, setReservationSearchLoading] = useState(false);
  const [reservationSearchError, setReservationSearchError] = useState("");
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [openingDraftId, setOpeningDraftId] = useState(null);
  const [sessionSyncTimedOut, setSessionSyncTimedOut] = useState(false);
  const [todayIso, setTodayIso] = useState(getTodayIsoDate);
  const createModalTriggerRef = useRef(null);
  const createModalRef = useRef(null);
  const guestNameInputRef = useRef(null);
  const waitlistSearchRef = useRef(null);
  const removalTriggerRef = useRef(null);
  const removalModalRef = useRef(null);
  const removalCancelButtonRef = useRef(null);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  useEffect(() => {
    const syncSessionFromStorage = () => setCurrentUser(readStoredUser());
    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    window.addEventListener("storage", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
      window.removeEventListener("storage", syncSessionFromStorage);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLinkedSearch(linkedSearch.trim()), 250);
    return () => clearTimeout(timer);
  }, [linkedSearch]);

  useEffect(() => {
    const timer = window.setInterval(() => setTodayIso(getTodayIsoDate()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!businessUnitId || sessionBusinessUnitReady) {
      setSessionSyncTimedOut(false);
      return undefined;
    }
    setSessionSyncTimedOut(false);
    const timer = window.setTimeout(() => setSessionSyncTimedOut(true), 8000);
    return () => window.clearTimeout(timer);
  }, [businessUnitId, sessionBusinessUnitReady]);

  useEffect(() => {
    setShowModal(false);
    setPendingRemoval(null);
    setEntryType("manual");
    setManualForm(defaultManualForm);
    setLinkedSearch("");
    setDebouncedLinkedSearch("");
    setReservationResults([]);
    setReservationSearchError("");
    setSelectedReservation(null);
    setModalError("");
  }, [businessUnitId]);

  useEffect(() => {
    if (!businessUnitId) {
      setRows([]);
      setLoading(false);
      return;
    }

    if (!sessionBusinessUnitReady) {
      setRows([]);
      setLoading(!sessionSyncTimedOut);
      setError(
        sessionSyncTimedOut
          ? waitlistCopy.messages?.businessUnitSyncError ||
              "The business unit session could not be updated."
          : ""
      );
      return;
    }

    if (!canViewWaitlist) {
      setRows([]);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");
    setWarningMessage("");

    axios
      .get("/api/waitlist", { params: { business_unit_id: businessUnitId } })
      .then((waitlistRes) => {
        if (!active) return;
        setRows(Array.isArray(waitlistRes.data) ? waitlistRes.data : []);
      })
      .catch((err) => {
        if (!active) return;
        setError(
          getLocalizedRequestError(
            err,
            waitlistCopy.messages?.loadError || "Error loading waitlist",
            languageId
          )
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    businessUnitId,
    canViewWaitlist,
    crossTabRefreshToken,
    sessionBusinessUnitReady,
    sessionSyncTimedOut,
    languageId,
    waitlistCopy.messages,
  ]);

  useEffect(() => {
    if (!businessUnitId) {
      setCategories([]);
      setCategoriesLoading(false);
      setCategoriesError(false);
      return;
    }

    if (!canCreateWaitlist) {
      setCategories([]);
      setCategoriesLoading(false);
      setCategoriesError(false);
      return;
    }

    if (!sessionBusinessUnitReady) {
      setCategories([]);
      setCategoriesLoading(!sessionSyncTimedOut);
      setCategoriesError(sessionSyncTimedOut);
      return;
    }

    let active = true;
    setCategoriesLoading(true);
    setCategoriesError(false);
    axios
      .get("/api/room_categories", { params: { business_unit_id: businessUnitId } })
      .then((categoriesRes) => {
        if (!active) return;
        setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      })
      .catch(() => {
        if (!active) return;
        setCategories([]);
        setCategoriesError(true);
      })
      .finally(() => {
        if (active) setCategoriesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    businessUnitId,
    canCreateWaitlist,
    categoryReloadToken,
    sessionBusinessUnitReady,
    sessionSyncTimedOut,
  ]);

  useEffect(() => {
    if (
      !showModal ||
      entryType !== "reservation_linked" ||
      !businessUnitId ||
      !canViewWaitlist ||
      !sessionBusinessUnitReady
    ) {
      setReservationResults([]);
      setReservationSearchLoading(false);
      setReservationSearchError("");
      return;
    }

    if (debouncedLinkedSearch.length < 2) {
      setReservationResults([]);
      setReservationSearchLoading(false);
      setReservationSearchError("");
      return;
    }

    if (debouncedLinkedSearch !== linkedSearch.trim()) {
      setReservationResults([]);
      setReservationSearchLoading(false);
      setReservationSearchError("");
      return;
    }

    let active = true;
    setReservationSearchLoading(true);
    setReservationSearchError("");

    axios
      .get("/api/waitlist/search-reservations", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedLinkedSearch,
        },
      })
      .then((res) => {
        if (!active) return;
        setReservationResults(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!active) return;
        setReservationResults([]);
        setReservationSearchError(
          waitlistCopy.modal?.searchReservationsError ||
            "Reservations could not be searched. Please try again."
        );
      })
      .finally(() => {
        if (active) setReservationSearchLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    businessUnitId,
    canViewWaitlist,
    debouncedLinkedSearch,
    entryType,
    linkedSearch,
    showModal,
    sessionBusinessUnitReady,
    waitlistCopy.modal,
  ]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(""), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const visibleRows = rows;

  const minimumCheckoutDate = manualForm.check_in_date
    ? addDaysToIsoDate(manualForm.check_in_date, 1)
    : todayIso;
  const manualDatesInvalid = Boolean(
    manualForm.check_in_date &&
      manualForm.check_out_date &&
      manualForm.check_out_date <= manualForm.check_in_date
  );
  const manualCheckInPast = Boolean(
    manualForm.check_in_date && manualForm.check_in_date < todayIso
  );
  const manualEmailInvalid = Boolean(
    manualForm.guest_email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualForm.guest_email.trim())
  );
  const manualFormValid = Boolean(
    manualForm.guest_name.trim() &&
      manualForm.room_category_id &&
      manualForm.check_in_date &&
      manualForm.check_out_date &&
      !manualDatesInvalid &&
      !manualCheckInPast &&
      !manualEmailInvalid
  );

  const statusCounts = useMemo(
    () =>
      visibleRows.reduce(
        (counts, row) => ({
          ...counts,
          [row.status]: (counts[row.status] || 0) + 1,
        }),
        { all: visibleRows.length }
      ),
    [visibleRows]
  );

  const filteredRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return visibleRows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!needle) return true;
      const haystack = [
        row.guest_name,
        row.room_category_name,
        row.reservation_locator,
        row.status,
        row.entry_type,
        getWaitlistStatusLabel(row.status, waitlistCopy),
        getEntryTypeLabel(row.entry_type, waitlistCopy),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [searchText, statusFilter, visibleRows, waitlistCopy]);

  const localizedStatusFilters = useMemo(
    () =>
      STATUS_FILTERS.map((filter) => ({
        key: filter.key,
        label:
          waitlistCopy?.statusFilters?.[filter.copyKey] ||
          (filter.key === "all" ? "All" : getWaitlistStatusLabel(filter.key, waitlistCopy)),
      })),
    [waitlistCopy]
  );

  const resetModalState = () => {
    setEntryType("manual");
    setManualForm(defaultManualForm);
    setLinkedSearch("");
    setDebouncedLinkedSearch("");
    setReservationResults([]);
    setReservationSearchError("");
    setSelectedReservation(null);
    setModalError("");
  };

  const handleEntryTypeChange = (nextEntryType) => {
    setEntryType(nextEntryType);
    setSelectedReservation(null);
    setLinkedSearch("");
    setReservationResults([]);
    setReservationSearchError("");
    setModalError("");
  };

  const handleEntryTypeKeyDown = (event) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    const options = Array.from(
      event.currentTarget.parentElement?.querySelectorAll('[role="radio"]') || []
    );
    if (options.length < 2) return;
    event.preventDefault();
    const currentIndex = options.indexOf(event.currentTarget);
    const direction = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
    const nextOption = options[(currentIndex + direction + options.length) % options.length];
    nextOption.focus();
    nextOption.click();
  };

  const handleOpenModal = () => {
    resetModalState();
    setShowModal(true);
  };

  const closeCreateModal = () => {
    setShowModal(false);
    resetModalState();
  };

  const handleCloseModal = () => {
    if (submitting) return;
    closeCreateModal();
  };

  useEffect(() => {
    if (!showModal) return undefined;

    const triggerElement = createModalTriggerRef.current;
    const focusFrame = window.requestAnimationFrame(() => {
      (guestNameInputRef.current || createModalRef.current)?.focus();
    });

    const handleFocusTrap = (event) => {
      if (event.key !== "Tab" || !createModalRef.current) return;
      const focusable = Array.from(
        createModalRef.current.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)'
        )
      ).filter((element) => !element.hasAttribute("hidden"));
      if (!focusable.length) {
        event.preventDefault();
        createModalRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleFocusTrap);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleFocusTrap);
      triggerElement?.focus();
    };
  }, [showModal]);

  useEffect(() => {
    if (!pendingRemoval) return undefined;

    const triggerElement = removalTriggerRef.current;
    const fallbackSearchElement = waitlistSearchRef.current;
    const focusFrame = window.requestAnimationFrame(() => {
      removalCancelButtonRef.current?.focus();
    });

    const handleFocusTrap = (event) => {
      if (event.key !== "Tab" || !removalModalRef.current) return;
      const focusable = Array.from(
        removalModalRef.current.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)'
        )
      ).filter((element) => !element.hasAttribute("hidden"));
      if (!focusable.length) {
        event.preventDefault();
        removalModalRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleFocusTrap);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleFocusTrap);
      if (triggerElement?.isConnected) triggerElement.focus();
      else fallbackSearchElement?.focus();
    };
  }, [pendingRemoval]);

  useEffect(() => {
    if (showModal && submitting) createModalRef.current?.focus();
  }, [showModal, submitting]);

  useEffect(() => {
    if (pendingRemoval && removingId) removalModalRef.current?.focus();
  }, [pendingRemoval, removingId]);

  useEffect(() => {
    if (!showModal && !pendingRemoval) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (pendingRemoval && !removingId) {
        setPendingRemoval(null);
        return;
      }
      if (showModal && !submitting) setShowModal(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [pendingRemoval, removingId, showModal, submitting]);

  const refreshWaitlist = async () => {
    if (!canViewWaitlist || !sessionBusinessUnitReady) return;
    const res = await axios.get("/api/waitlist", {
      params: { business_unit_id: businessUnitId },
    });
    setRows(Array.isArray(res.data) ? res.data : []);
  };

  const handleCreateEntry = async () => {
    if (!businessUnitId || !sessionBusinessUnitReady || submitting) return;
    if (entryType === "manual" && !manualFormValid) {
      setModalError(
        manualCheckInPast
          ? waitlistCopy.modal?.checkInPastError || "Check-in cannot be before today."
          : manualDatesInvalid
            ? waitlistCopy.modal?.dateOrderError || "Check-out must be after check-in."
            : manualEmailInvalid
              ? waitlistCopy.modal?.invalidEmailError ||
                "Enter a valid email address or leave the field empty."
              : waitlistCopy.modal?.completeRequiredFields ||
                "Complete the required fields before creating the entry."
      );
      return;
    }
    if (entryType === "reservation_linked" && !selectedReservation) return;

    setSubmitting(true);
    setModalError("");
    setError("");
    setWarningMessage("");

    try {
      const payload =
        entryType === "manual"
          ? {
              business_unit_id: Number(businessUnitId),
              entry_type: "manual",
              guest_name: manualForm.guest_name.trim(),
              guest_email: manualForm.guest_email.trim() || null,
              guest_phone: manualForm.guest_phone.trim() || null,
              room_category_id: Number(manualForm.room_category_id),
              check_in_date: manualForm.check_in_date,
              check_out_date: manualForm.check_out_date,
              notes: manualForm.notes.trim() || null,
            }
          : {
              business_unit_id: Number(businessUnitId),
              entry_type: "reservation_linked",
              reservation_id: selectedReservation?.id,
              notes: manualForm.notes.trim() || null,
            };

      await axios.post("/api/waitlist", payload);
      try {
        await refreshWaitlist();
        setError("");
        setWarningMessage("");
      } catch {
        setWarningMessage(
          waitlistCopy.messages?.refreshWarning ||
            "The action succeeded, but the list could not refresh."
        );
      }
      closeCreateModal();
      setSuccessMessage(
        waitlistCopy.messages?.createSuccess || "Waitlist entry created successfully."
      );
    } catch (err) {
      const serverMessage = String(err?.response?.data?.error || "");
      setModalError(
        getLocalizedRequestError(
          err,
          serverMessage.startsWith("Reservation already has an active waitlist entry")
            ? waitlistCopy.messages?.duplicateEntry ||
                "This reservation already has an active waitlist entry."
            : waitlistCopy.messages?.createError || "Error creating waitlist entry",
          languageId
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveEntry = async (rowId) => {
    if (!sessionBusinessUnitReady || removingId || openingDraftId) return;
    setRemovingId(rowId);
    setError("");
    setWarningMessage("");
    try {
      await axios.post(`/api/waitlist/${rowId}/cancel`);
      try {
        await refreshWaitlist();
        setError("");
        setWarningMessage("");
      } catch {
        setWarningMessage(
          waitlistCopy.messages?.refreshWarning ||
            "The action succeeded, but the list could not refresh."
        );
      }
      setSuccessMessage(waitlistCopy.messages?.removeSuccess || "Waitlist entry removed.");
      setPendingRemoval(null);
    } catch (err) {
      const serverMessage = String(err?.response?.data?.error || "");
      const localizedFallback = serverMessage.startsWith(
        "This waitlist entry is currently protecting an open reservation draft"
      )
        ? waitlistCopy.messages?.openDraftRemovalConflict ||
          "This entry is protecting an open reservation draft."
        : serverMessage === "Only pending or blocked waitlist entries can be removed"
          ? waitlistCopy.messages?.removeStateConflict ||
            "Only waiting entries can be removed."
          : waitlistCopy.messages?.removeError || "Error removing waitlist entry";
      setError(
        getLocalizedRequestError(err, localizedFallback, languageId)
      );
      setPendingRemoval(null);
    } finally {
      setRemovingId(null);
    }
  };

  const handleCreateReservationFromWaitlist = async (row) => {
    if (
      !row?.block_id ||
      !businessUnitId ||
      !sessionBusinessUnitReady ||
      openingDraftId ||
      removingId
    ) return;

    setOpeningDraftId(row.id);
    setError("");
    try {
      const res = await axios.post("/api/reservation-drafts/create", {
        number_of_rooms: 1,
        created_by: "WaitlistCreateReservation",
        business_unit_id: Number(businessUnitId),
        source_block_id: Number(row.block_id),
        source_waitlist_entry_id: Number(row.id),
      });

      const reservationGroupId = res?.data?.reservation_group_id;
      if (!reservationGroupId) {
        throw new Error("Missing reservation_group_id in draft creation response");
      }

      navigate(`/reservation-group/${reservationGroupId}`);
    } catch (err) {
      console.error("[Waitlist][UI] Error opening reservation draft:", err);
      setError(
        getLocalizedRequestError(
          err,
          waitlistCopy.messages?.openDraftError || "Error opening reservation draft",
          languageId
        )
      );
    } finally {
      setOpeningDraftId(null);
    }
  };

  if (!businessUnitId) {
    return (
      <div className={`wl-shell ${isLightTheme ? "wl-shell--light" : ""}`}>
        <div className="wl-card wl-businessUnitEmpty">
          <span className="wl-emptyIcon" aria-hidden="true">
            <FiUsers size={20} />
          </span>
          {waitlistCopy.selectBusinessUnit || "Select a business unit to manage waitlist entries."}
        </div>
      </div>
    );
  }

  return (
    <div className={`wl-shell ${isLightTheme ? "wl-shell--light" : ""}`}>
      <div className="wl-card">
        <div className="wl-toolbar">
          <div className="wl-headingCopy">
            <h2 className="wl-title">{waitlistCopy.title || "Waitlist"}</h2>
            <p className="wl-copy">
              {waitlistCopy.description ||
                "Manual entries watch category availability. Linked entries only trigger when their exact reservation is really cancelled or deleted."}
            </p>
            <div className="wl-flow" aria-label={waitlistCopy.title || "Waitlist"}>
              <span className="wl-flowStep">
                {getWaitlistStatusLabel("Pending", waitlistCopy)}
              </span>
              <FiArrowRight className="wl-flowArrow" size={13} aria-hidden="true" />
              <span className="wl-flowStep wl-flowStep--ready">
                {getWaitlistStatusLabel("Blocked", waitlistCopy)}
              </span>
              <FiArrowRight className="wl-flowArrow" size={13} aria-hidden="true" />
              <span className="wl-flowStep wl-flowStep--action">
                {waitlistCopy.actions?.createReservation || "Create reservation"}
              </span>
              <FiArrowRight className="wl-flowArrow" size={13} aria-hidden="true" />
              <span className="wl-flowStep wl-flowStep--complete">
                <FiCheckCircle size={12} aria-hidden="true" />
                {getWaitlistStatusLabel("Converted", waitlistCopy)}
              </span>
            </div>
          </div>
          <div className="wl-toolbarActions">
            {canCreateWaitlist ? (
              <button
                ref={createModalTriggerRef}
                className="wl-btn wl-btn--primary"
                type="button"
                disabled={!sessionBusinessUnitReady || Boolean(openingDraftId)}
                onClick={handleOpenModal}
              >
                <FiPlus size={15} aria-hidden="true" />
                {waitlistCopy.newEntry || "New Waitlist Entry"}
              </button>
            ) : null}
          </div>
        </div>

        <div className="wl-filters">
          <label className="wl-searchField">
            <FiSearch size={16} aria-hidden="true" />
            <input
              ref={waitlistSearchRef}
              className="wl-search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              aria-label={
                waitlistCopy.searchPlaceholder ||
                "Search guest, category, reservation or status..."
              }
              placeholder={
                waitlistCopy.searchPlaceholder ||
                "Search guest, category, reservation or status..."
              }
            />
          </label>
          <div className="wl-filterRow">
            {localizedStatusFilters.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={`wl-filterBtn ${statusFilter === filter.key ? "wl-filterBtn--active" : ""}`}
                aria-pressed={statusFilter === filter.key}
                onClick={() => setStatusFilter(filter.key)}
              >
                {filter.label}
                <span className="wl-filterCount">
                  {statusCounts[filter.key === "all" ? "all" : filter.key] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {successMessage ? (
          <div className="wl-alert wl-success" role="status">
            <FiCheckCircle size={16} aria-hidden="true" />
            <span>{successMessage}</span>
          </div>
        ) : null}
        {error ? (
          <div className="wl-alert wl-error" role="alert">
            <FiAlertCircle size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        ) : null}
        {warningMessage ? (
          <div className="wl-alert wl-warning" role="status">
            <FiAlertCircle size={16} aria-hidden="true" />
            <span>{warningMessage}</span>
          </div>
        ) : null}
        {loading ? (
          <div className="wl-loading" role="status">
            <span className="wl-spinner" aria-hidden="true" />
            <span>{waitlistCopy.loading || "Loading waitlist..."}</span>
          </div>
        ) : null}

        {!sessionBusinessUnitReady ? null : !canViewWaitlist ? (
          <div className="wl-empty">
            <span className="wl-emptyIcon" aria-hidden="true">
              <FiAlertCircle size={20} />
            </span>
            <span>
              {language?.messages?.reservationsTabs?.noPermission ||
                "You don't have permission to view reservations."}
            </span>
          </div>
        ) : !loading ? (
          filteredRows.length ? (
            <div className="wl-tableWrap">
              <table className="wl-table">
                <thead>
                  <tr>
                    <th>{waitlistCopy.table?.guest || "Guest"}</th>
                    <th>{waitlistCopy.table?.roomCategory || "Room Category"}</th>
                    <th>{waitlistCopy.table?.checkIn || "Check-in"}</th>
                    <th>{waitlistCopy.table?.checkOut || "Check-out"}</th>
                    <th>{waitlistCopy.table?.linkedReservation || "Linked Reservation"}</th>
                    <th>{waitlistCopy.table?.status || "Status"}</th>
                    <th>{waitlistCopy.table?.type || "Type"}</th>
                    <th>{waitlistCopy.table?.actions || "Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id} className={`wl-row ${getWaitlistRowToneClass(row.status)}`}>
                      <td data-label={waitlistCopy.table?.guest || "Guest"}>
                        <span>
                          <strong className="wl-guestName">{row.guest_name || "-"}</strong>
                        {row.guest_email ? <div className="wl-muted">{row.guest_email}</div> : null}
                        </span>
                      </td>
                      <td data-label={waitlistCopy.table?.roomCategory || "Room Category"}>
                        {row.room_category_name || "-"}
                      </td>
                      <td data-label={waitlistCopy.table?.checkIn || "Check-in"}>
                        {formatDisplayDate(row.check_in_date)}
                      </td>
                      <td data-label={waitlistCopy.table?.checkOut || "Check-out"}>
                        {formatDisplayDate(row.check_out_date)}
                      </td>
                      <td data-label={waitlistCopy.table?.linkedReservation || "Linked Reservation"}>
                        {row.reservation_locator || "-"}
                      </td>
                      <td data-label={waitlistCopy.table?.status || "Status"}>
                        <span className={`wl-pill wl-pill--${getWaitlistStatusClass(row.status)}`}>
                          {getWaitlistStatusLabel(row.status, waitlistCopy)}
                        </span>
                      </td>
                      <td data-label={waitlistCopy.table?.type || "Type"}>
                        <span
                          className={`wl-typePill ${
                            row.entry_type === "reservation_linked"
                              ? "wl-typePill--linked"
                              : ""
                          }`}
                        >
                          {row.entry_type === "reservation_linked" ? (
                            <FiLink2 size={12} aria-hidden="true" />
                          ) : (
                            <FiUser size={12} aria-hidden="true" />
                          )}
                          {getEntryTypeLabel(row.entry_type, waitlistCopy)}
                        </span>
                      </td>
                      <td data-label={waitlistCopy.table?.actions || "Actions"}>
                        {(row.status === "Pending" || row.status === "Blocked") &&
                        (canUpdateWaitlist ||
                          (canCreateWaitlist && row.status === "Blocked" && row.block_id)) ? (
                          <div className="wl-actionRow">
                            {canCreateWaitlist && row.status === "Blocked" && row.block_id ? (
                              <button
                                className="wl-btn wl-btn--primary"
                                type="button"
                                disabled={Boolean(openingDraftId)}
                                aria-busy={openingDraftId === row.id}
                                onClick={() => handleCreateReservationFromWaitlist(row)}
                              >
                                <FiArrowRight size={13} aria-hidden="true" />
                                {waitlistCopy.actions?.createReservation || "Create reservation"}
                              </button>
                            ) : null}
                            {canUpdateWaitlist ? (
                              <button
                                className="wl-btn wl-btn--danger"
                                type="button"
                                disabled={Boolean(openingDraftId)}
                                aria-label={waitlistCopy.actions?.delete || "Remove from waitlist"}
                                onClick={(event) => {
                                  removalTriggerRef.current = event.currentTarget;
                                  setPendingRemoval(row);
                                }}
                              >
                                <FiTrash2 size={13} aria-hidden="true" />
                                {waitlistCopy.actions?.removeShort || "Remove"}
                              </button>
                            ) : null}
                          </div>
                        ) : row.status === "Converted" ? (
                          <span className="wl-muted">
                            {waitlistCopy.actions?.converted || "Converted"}
                          </span>
                        ) : (
                          <span className="wl-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : error ? null : (
            <div className="wl-empty">
              <span className="wl-emptyIcon" aria-hidden="true">
                <FiUsers size={20} />
              </span>
              <span>
                {waitlistCopy.empty || "No waitlist entries match the current filters."}
              </span>
            </div>
          )
        ) : null}
      </div>

      {showModal ? (
        <div
          className="wl-modalOverlay"
          onClick={() => {
            if (!submitting) handleCloseModal();
          }}
        >
          <form
            ref={createModalRef}
            className="wl-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wl-create-title"
            aria-busy={submitting}
            tabIndex={-1}
            onSubmit={(event) => {
              event.preventDefault();
              handleCreateEntry();
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="wl-modalHeader">
              <div>
                <h3 className="wl-modalTitle" id="wl-create-title">
                  {waitlistCopy.modal?.title || "Create Waitlist Entry"}
                </h3>
                <p className="wl-modalCopy">
                  {waitlistCopy.modal?.copy ||
                    "Manual entries reserve a category when enough space opens. Linked entries wait for one exact reservation to be cancelled or deleted."}
                </p>
              </div>
              <button
                className="wl-iconBtn"
                type="button"
                disabled={submitting}
                aria-label={waitlistCopy.actions?.close || "Close"}
                onClick={handleCloseModal}
              >
                <FiX size={17} aria-hidden="true" />
              </button>
            </div>

            <fieldset className="wl-modalBody" disabled={submitting}>
              {modalError ? (
                <div className="wl-alert wl-error" role="alert">
                  <FiAlertCircle size={16} aria-hidden="true" />
                  <span>{modalError}</span>
                </div>
              ) : null}

              <div className="wl-grid">
                <div className="wl-field wl-field--full">
                  <span className="wl-fieldLabel" id="wl-entry-type-label">
                    {waitlistCopy.modal?.waitlistType || "Waitlist Type"}
                  </span>
                  <div
                    className="wl-typeOptions"
                    role="radiogroup"
                    aria-labelledby="wl-entry-type-label"
                  >
                    <button
                      className={`wl-typeOption ${entryType === "manual" ? "isActive" : ""}`}
                      type="button"
                      role="radio"
                      aria-checked={entryType === "manual"}
                      tabIndex={entryType === "manual" ? 0 : -1}
                      onKeyDown={handleEntryTypeKeyDown}
                      onClick={() => handleEntryTypeChange("manual")}
                    >
                      <FiUser size={17} aria-hidden="true" />
                      <span>{getEntryTypeLabel("manual", waitlistCopy)}</span>
                    </button>
                    {canViewWaitlist ? (
                      <button
                        className={`wl-typeOption ${
                          entryType === "reservation_linked" ? "isActive" : ""
                        }`}
                        type="button"
                        role="radio"
                        aria-checked={entryType === "reservation_linked"}
                        tabIndex={entryType === "reservation_linked" ? 0 : -1}
                        onKeyDown={handleEntryTypeKeyDown}
                        onClick={() => handleEntryTypeChange("reservation_linked")}
                      >
                        <FiLink2 size={17} aria-hidden="true" />
                        <span>{getEntryTypeLabel("reservation_linked", waitlistCopy)}</span>
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {entryType === "manual" ? (
                <div className="wl-formPanel">
                  <div className="wl-grid">
                    <div className="wl-field">
                      <label htmlFor="wl-guest-name">
                        {waitlistCopy.modal?.guestName || "Guest Name"}
                        <span className="wl-required" aria-hidden="true">*</span>
                      </label>
                      <input
                        ref={guestNameInputRef}
                        id="wl-guest-name"
                        className="wl-input"
                        required
                        value={manualForm.guest_name}
                        onChange={(event) =>
                          setManualForm((current) => ({ ...current, guest_name: event.target.value }))
                        }
                        placeholder={waitlistCopy.modal?.guestNamePlaceholder || "Guest full name"}
                      />
                    </div>
                    <div className="wl-field">
                      <label htmlFor="wl-room-category">
                        {waitlistCopy.modal?.roomCategory || "Room Category"}
                        <span className="wl-required" aria-hidden="true">*</span>
                      </label>
                      <select
                        id="wl-room-category"
                        className="wl-select"
                        required
                        disabled={
                          categoriesLoading || categoriesError || categories.length === 0
                        }
                        value={manualForm.room_category_id}
                        onChange={(event) =>
                          setManualForm((current) => ({
                            ...current,
                            room_category_id: event.target.value,
                          }))
                        }
                      >
                        <option value="">
                          {categoriesLoading
                            ? waitlistCopy.modal?.loadingCategories || "Loading categories..."
                            : categoriesError
                              ? waitlistCopy.modal?.categoryLoadError ||
                                "Room categories could not be loaded."
                              : categories.length
                                ? waitlistCopy.modal?.selectCategory || "Select a category"
                                : waitlistCopy.modal?.noCategories ||
                                  "No room categories are available."}
                        </option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      {categoriesError ? (
                        <div className="wl-fieldRecovery" role="alert">
                          <span className="wl-fieldError">
                            {waitlistCopy.modal?.categoryLoadError ||
                              "Room categories could not be loaded."}
                          </span>
                          <button
                            className="wl-inlineButton"
                            type="button"
                            onClick={() => setCategoryReloadToken((current) => current + 1)}
                          >
                            {waitlistCopy.actions?.retry || "Retry"}
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="wl-field">
                      <label htmlFor="wl-check-in">
                        {waitlistCopy.modal?.checkIn || "Check-in"}
                        <span className="wl-required" aria-hidden="true">*</span>
                      </label>
                      <input
                        id="wl-check-in"
                        className="wl-input"
                        type="date"
                        required
                        min={todayIso}
                        aria-invalid={manualCheckInPast}
                        value={manualForm.check_in_date}
                        onChange={(event) => {
                          const nextCheckIn = event.target.value;
                          setManualForm((current) => ({
                            ...current,
                            check_in_date: nextCheckIn,
                            check_out_date:
                              current.check_out_date && current.check_out_date > nextCheckIn
                                ? current.check_out_date
                                : "",
                          }));
                        }}
                      />
                      {manualCheckInPast ? (
                        <span className="wl-fieldError" role="alert">
                          {waitlistCopy.modal?.checkInPastError ||
                            "Check-in cannot be before today."}
                        </span>
                      ) : null}
                    </div>
                    <div className="wl-field">
                      <label htmlFor="wl-check-out">
                        {waitlistCopy.modal?.checkOut || "Check-out"}
                        <span className="wl-required" aria-hidden="true">*</span>
                      </label>
                      <input
                        id="wl-check-out"
                        className="wl-input"
                        type="date"
                        required
                        min={minimumCheckoutDate}
                        aria-invalid={manualDatesInvalid}
                        value={manualForm.check_out_date}
                        onChange={(event) =>
                          setManualForm((current) => ({ ...current, check_out_date: event.target.value }))
                        }
                      />
                      {manualDatesInvalid ? (
                        <span className="wl-fieldError" role="alert">
                          {waitlistCopy.modal?.dateOrderError ||
                            "Check-out must be after check-in."}
                        </span>
                      ) : null}
                    </div>
                    <div className="wl-field">
                      <label htmlFor="wl-email">
                        {waitlistCopy.modal?.email || "Email"}
                      </label>
                      <input
                        id="wl-email"
                        className="wl-input"
                        type="email"
                        aria-invalid={manualEmailInvalid}
                        value={manualForm.guest_email}
                        onChange={(event) =>
                          setManualForm((current) => ({ ...current, guest_email: event.target.value }))
                        }
                        placeholder={waitlistCopy.modal?.emailPlaceholder || "Optional email"}
                      />
                      {manualEmailInvalid ? (
                        <span className="wl-fieldError" role="alert">
                          {waitlistCopy.modal?.invalidEmailError ||
                            "Enter a valid email address or leave the field empty."}
                        </span>
                      ) : null}
                    </div>
                    <div className="wl-field">
                      <label htmlFor="wl-phone">
                        {waitlistCopy.modal?.phone || "Phone"}
                      </label>
                      <input
                        id="wl-phone"
                        className="wl-input"
                        type="tel"
                        value={manualForm.guest_phone}
                        onChange={(event) =>
                          setManualForm((current) => ({ ...current, guest_phone: event.target.value }))
                        }
                        placeholder={waitlistCopy.modal?.phonePlaceholder || "Optional phone"}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="wl-formPanel">
                  <div className="wl-field">
                    <label htmlFor="wl-reservation-search">
                      {waitlistCopy.modal?.searchReservation || "Search Reservation"}
                    </label>
                    <input
                      id="wl-reservation-search"
                      className="wl-input"
                      value={linkedSearch}
                      onChange={(event) => {
                        setLinkedSearch(event.target.value);
                        setSelectedReservation(null);
                        setReservationResults([]);
                        setReservationSearchError("");
                      }}
                      placeholder={
                        waitlistCopy.modal?.searchReservationPlaceholder ||
                        "Search by guest name, email or phone..."
                      }
                    />
                  </div>

                  {selectedReservation ? (
                    <div className="wl-selected">
                      <div className="wl-selectedHead">
                        <FiCheckCircle size={15} aria-hidden="true" />
                        <strong>{selectedReservation.guest_name}</strong>
                      </div>
                      <div className="wl-resultMeta">
                        {selectedReservation.locator}
                        {" \u00b7 "}
                        {buildStayLabel(selectedReservation)}
                        {" \u00b7 "}
                        {selectedReservation.room_category_name}
                      </div>
                      <div className="wl-resultMeta">
                        {(waitlistCopy.selection?.statusPrefix || "Status") + ": "}
                        {translateReservationStatus(selectedReservation.status, languageId) || "-"}
                      </div>
                      {selectedReservation.room_number ? (
                        <div className="wl-resultMeta">
                          {(waitlistCopy.selection?.roomPrefix || "Room") + " "}
                          {selectedReservation.room_number}
                        </div>
                      ) : null}
                      {selectedReservation.is_split_stay ? (
                        <div className="wl-resultNotice">
                          {waitlistCopy.selection?.splitStay ||
                            "Split stay: an entry will be created for each stay segment."}
                        </div>
                      ) : null}
                      <div className="wl-selectedActions">
                        <button
                          className="wl-btn wl-btn--ghost"
                          type="button"
                          onClick={() => setSelectedReservation(null)}
                        >
                          {waitlistCopy.actions?.changeReservation || "Change reservation"}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {reservationSearchLoading ? (
                    <div className="wl-muted">
                      {waitlistCopy.modal?.searchingReservations || "Searching reservations..."}
                    </div>
                  ) : null}

                  {reservationSearchError ? (
                    <div className="wl-inlineError" role="alert">
                      <FiAlertCircle size={14} aria-hidden="true" />
                      <span>{reservationSearchError}</span>
                    </div>
                  ) : null}

                  {debouncedLinkedSearch.length >= 2 &&
                  !selectedReservation &&
                  !reservationSearchLoading &&
                  !reservationSearchError ? (
                    <div className="wl-searchResults">
                      {reservationResults.length ? (
                        reservationResults.map((reservation) => (
                          <div
                            key={reservation.id}
                            className={`wl-resultCard ${
                              reservation.selectable ? "" : "wl-resultCard--disabled"
                            }`}
                          >
                            <div>
                              <p className="wl-resultTitle">{reservation.guest_name}</p>
                              <div className="wl-resultMeta">
                                {reservation.locator}
                                {" \u00b7 "}
                                {buildStayLabel(reservation)}
                                {" \u00b7 "}
                                {reservation.room_category_name ||
                                  waitlistCopy.selection?.noCategory ||
                                  "No category"}
                              </div>
                              <div className="wl-resultMeta">
                                {(waitlistCopy.selection?.statusPrefix || "Status") + ": "}
                                {translateReservationStatus(reservation.status, languageId) || "-"}
                              </div>
                              <div className="wl-resultMeta">
                                {reservation.guest_email ||
                                  waitlistCopy.selection?.noEmail ||
                                  "No email"}
                                {" \u00b7 "}
                                {reservation.guest_phone ||
                                  waitlistCopy.selection?.noPhone ||
                                  "No phone"}
                              </div>
                              {reservation.is_split_stay ? (
                                <div className="wl-resultNotice">
                                  {waitlistCopy.selection?.splitStay ||
                                    "Split stay: an entry will be created for each stay segment."}
                                </div>
                              ) : null}
                              {!reservation.selectable && reservation.disabled_reason ? (
                                <div className="wl-resultReason">
                                  {getDisabledReasonLabel(
                                    reservation.disabled_reason,
                                    waitlistCopy
                                  )}
                                </div>
                              ) : null}
                            </div>
                            <div>
                              <button
                                className="wl-btn wl-btn--primary"
                                type="button"
                                disabled={!reservation.selectable}
                                onClick={() => setSelectedReservation(reservation)}
                              >
                                <FiCheckCircle size={13} aria-hidden="true" />
                                {waitlistCopy.actions?.select || "Select"}
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="wl-muted">
                          {waitlistCopy.modal?.noReservations ||
                            "No reservations found for that search."}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
              <div className="wl-field">
                <label htmlFor="wl-notes">{waitlistCopy.modal?.notes || "Notes"}</label>
                <input
                  id="wl-notes"
                  className="wl-input"
                  value={manualForm.notes}
                  onChange={(event) =>
                    setManualForm((current) => ({ ...current, notes: event.target.value }))
                  }
                  placeholder={waitlistCopy.modal?.notesPlaceholder || "Optional internal note..."}
                />
              </div>
            </fieldset>
            <div className="wl-modalFooter">
              <button
                className="wl-btn wl-btn--ghost"
                type="button"
                disabled={submitting}
                onClick={handleCloseModal}
              >
                {waitlistCopy.actions?.cancel || "Cancel"}
              </button>
              <button
                className="wl-btn wl-btn--primary"
                type="submit"
                disabled={
                  submitting ||
                  (entryType === "manual" && !manualFormValid) ||
                  (entryType === "reservation_linked" && !selectedReservation)
                }
              >
                <FiPlus size={14} aria-hidden="true" />
                {submitting
                  ? waitlistCopy.modal?.creating || "Creating..."
                  : waitlistCopy.modal?.create || "Create Waitlist Entry"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {pendingRemoval ? (
        <div
          className="wl-modalOverlay"
          onClick={() => {
            if (!removingId) setPendingRemoval(null);
          }}
        >
          <div
            ref={removalModalRef}
            className="wl-modal wl-confirmModal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="wl-remove-title"
            aria-describedby="wl-remove-description"
            tabIndex={-1}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="wl-modalHeader">
              <div>
                <h3 className="wl-modalTitle" id="wl-remove-title">
                  {waitlistCopy.modal?.removeTitle || "Remove waitlist entry?"}
                </h3>
                <p className="wl-modalCopy">{pendingRemoval.guest_name || "-"}</p>
              </div>
              <button
                className="wl-iconBtn"
                type="button"
                disabled={Boolean(removingId)}
                aria-label={waitlistCopy.actions?.close || "Close"}
                onClick={() => setPendingRemoval(null)}
              >
                <FiX size={17} aria-hidden="true" />
              </button>
            </div>
            <div className="wl-modalBody">
              <p className="wl-confirmCopy" id="wl-remove-description">
                {waitlistCopy.modal?.removeCopy ||
                  "The entry and any availability hold linked to it will be removed. This action cannot be undone."}
              </p>
            </div>
            <div className="wl-modalFooter">
              <button
                ref={removalCancelButtonRef}
                className="wl-btn wl-btn--ghost"
                type="button"
                disabled={Boolean(removingId)}
                onClick={() => setPendingRemoval(null)}
              >
                {waitlistCopy.actions?.cancel || "Cancel"}
              </button>
              <button
                className="wl-btn wl-btn--danger"
                type="button"
                disabled={Boolean(removingId)}
                aria-busy={Boolean(removingId)}
                onClick={() => handleRemoveEntry(pendingRemoval.id)}
              >
                <FiTrash2 size={14} aria-hidden="true" />
                {removingId
                  ? waitlistCopy.modal?.removing || "Removing..."
                  : waitlistCopy.modal?.removeConfirm || "Remove entry"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ReservationsWaitlist;
